import {
  ArrowLeft,
  DotsThree,
  MagnifyingGlass,
  Plus,
  XCircle,
  X,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";

import { navigateToAppPath } from "../navigation";

export function NewCustomerPage() {
  const [isAddressOpen, setIsAddressOpen] = useState(false);
  const [tagDraft, setTagDraft] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [saveState, setSaveState] = useState<"idle" | "invalid" | "ready">(
    "idle",
  );

  useEffect(() => {
    if (!isAddressOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsAddressOpen(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isAddressOpen]);

  const addTag = () => {
    const nextTag = tagDraft.trim().replace(/,$/, "").trim();
    if (!nextTag || tags.includes(nextTag)) return;
    setTags((currentTags) => [...currentTags, nextTag]);
    setTagDraft("");
  };

  const removeTag = (tagToRemove: string) => {
    setTags((currentTags) => currentTags.filter((tag) => tag !== tagToRemove));
  };

  return (
    <main aria-label="New customer" className="new-customer-workspace">
      <header className="new-customer-header">
        <button
          className="new-customer-back"
          onClick={() => navigateToAppPath("/app/customers")}
          type="button"
        >
          <ArrowLeft aria-hidden="true" size={18} />
        </button>
      </header>

      <div className="new-customer-layout">
        <section className="new-customer-main">
          <h1>New customer</h1>

          <section aria-labelledby="customer-overview-heading">
            <h2 id="customer-overview-heading">Customer overview</h2>
            <form
              className="new-customer-form"
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                setSaveState(
                  event.currentTarget.reportValidity() ? "ready" : "invalid",
                );
              }}
            >
              <div className="new-customer-form-panel">
                <div className="new-customer-two-up">
                  <label>
                    First name
                    <input
                      autoComplete="given-name"
                      name="firstName"
                      required
                    />
                  </label>
                  <label>
                    Last name
                    <input
                      autoComplete="family-name"
                      name="lastName"
                      required
                    />
                  </label>
                </div>
                <label>
                  Language
                  <select defaultValue="English" name="language">
                    <option>English</option>
                    <option>isiZulu</option>
                    <option>Sesotho</option>
                    <option>Setswana</option>
                  </select>
                  <small>
                    This customer will receive notifications in this language.
                  </small>
                </label>
                <label>
                  Email
                  <span className="new-customer-input-with-icon">
                    <input autoComplete="email" name="email" type="email" />
                    <DotsThree aria-hidden="true" size={22} weight="bold" />
                  </span>
                </label>
                <label>
                  Phone number
                  <span className="new-customer-phone-input">
                    <select aria-label="Country code" defaultValue="+27">
                      <option value="+27">🇿🇦 +27</option>
                    </select>
                    <input
                      autoComplete="tel"
                      name="phoneNumber"
                      required
                      type="tel"
                    />
                  </span>
                </label>
                <fieldset className="new-customer-consents">
                  <label>
                    <input type="checkbox" /> Customer agreed to receive
                    marketing emails.
                  </label>
                  <label>
                    <input type="checkbox" /> Customer agreed to receive SMS
                    marketing text messages.
                  </label>
                  <label>
                    <input type="checkbox" /> Customer agreed to receive
                    WhatsApp marketing messages.
                  </label>
                </fieldset>
              </div>
              <div className="new-customer-form-actions">
                {saveState === "invalid" ? (
                  <p className="new-customer-validation-message" role="status">
                    Add a first name, last name, and phone number to continue.
                  </p>
                ) : null}
                {saveState === "ready" ? (
                  <p className="new-customer-validation-message" role="status">
                    Required details are complete.
                  </p>
                ) : null}
                <button className="new-customer-save" type="submit">
                  Save customer
                </button>
              </div>
            </form>
          </section>

          <section
            aria-labelledby="default-address-heading"
            className="new-customer-address"
          >
            <h2 id="default-address-heading">Default address</h2>
            <p>The primary address of this customer</p>
            <button onClick={() => setIsAddressOpen(true)} type="button">
              <Plus aria-hidden="true" size={19} />
              Add address
              <span aria-hidden="true">›</span>
            </button>
          </section>
        </section>

        <aside aria-label="Customer details" className="new-customer-sidebar">
          <section className="new-customer-sidebar-section">
            <h2>Tags</h2>
            <div className="new-customer-tags-control">
              <Plus aria-hidden="true" size={15} />
              <div className="new-customer-tag-list">
                {tags.map((tag) => (
                  <span className="new-customer-tag" key={tag}>
                    {tag}
                    <button
                      aria-label={`Remove ${tag}`}
                      onClick={() => removeTag(tag)}
                      type="button"
                    >
                      <XCircle aria-hidden="true" size={14} />
                    </button>
                  </span>
                ))}
                <input
                  aria-label="Add tag"
                  onChange={(event) => setTagDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === ",") {
                      event.preventDefault();
                      addTag();
                    }
                  }}
                  onBlur={addTag}
                  placeholder={tags.length ? "Add another" : "Add tags"}
                  value={tagDraft}
                />
              </div>
            </div>
          </section>
        </aside>
      </div>

      {isAddressOpen ? (
        <div
          aria-label="Add default address"
          className="new-customer-modal-backdrop"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) setIsAddressOpen(false);
          }}
          role="presentation"
        >
          <section
            aria-labelledby="add-address-heading"
            aria-modal="true"
            className="new-customer-address-modal"
            role="dialog"
          >
            <header className="new-customer-modal-header">
              <h2 id="add-address-heading">Add default address</h2>
              <button
                aria-label="Close address dialog"
                className="new-customer-modal-close"
                onClick={() => setIsAddressOpen(false)}
                type="button"
              >
                <X aria-hidden="true" size={19} />
              </button>
            </header>

            <div className="new-customer-address-fields">
              <label>
                Country/region
                <select defaultValue="South Africa">
                  <option>South Africa</option>
                  <option>Lesotho</option>
                  <option>Botswana</option>
                </select>
              </label>
              <label>
                Address
                <span className="new-customer-input-with-icon">
                  <input autoComplete="street-address" />
                  <MagnifyingGlass aria-hidden="true" size={21} />
                </span>
              </label>
              <label>
                Suburb
                <input />
              </label>
              <div className="new-customer-address-three-up">
                <label>
                  City
                  <input autoComplete="address-level2" />
                </label>
                <label>
                  Province
                  <select defaultValue="">
                    <option value="">Select a province</option>
                    <option>Gauteng</option>
                    <option>Western Cape</option>
                    <option>KwaZulu-Natal</option>
                  </select>
                </label>
                <label>
                  Postal code
                  <input autoComplete="postal-code" />
                </label>
              </div>
            </div>

            <footer className="new-customer-modal-actions">
              <button
                className="new-customer-modal-cancel"
                onClick={() => setIsAddressOpen(false)}
                type="button"
              >
                Cancel
              </button>
              <button className="new-customer-modal-save" type="button">
                Save
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </main>
  );
}
